import {
  addDoc,
  collection,
  getDocs,
  query,
  serverTimestamp,
  Timestamp,
  where,
} from "firebase/firestore";
import AIService from "./AIService";
import { db } from "../firebase/config";
import type { App } from "../types/app";
import type {
  GooglePlayReleaseNotesReport,
  GooglePlayReleaseNotesTemplateKey,
} from "../types/googlePlayReleaseNotes";
import {
  GOOGLE_PLAY_STORE_LOCALES,
  getStoreLocaleDisplayLabel,
} from "../utils/storeLocales";
import { buildAppContextLines } from "../utils/appContextPrompt";

const REPORTS_COLLECTION = "googlePlayReleaseNotesReports";
const DEFAULT_MODEL = "gemini-2.5-flash-lite";
const MAX_LOCALES_PER_BATCH = 20;
const BATCH_RETRY_COUNT = 2;
const GOOGLE_PLAY_SUPPORTED_LOCALE_CODES = new Set(
  GOOGLE_PLAY_STORE_LOCALES.map((locale) => locale.code.toLowerCase()),
);

const TEMPLATE_LIBRARY: Record<
  Exclude<GooglePlayReleaseNotesTemplateKey, "custom">,
  { label: string; baseText: string }
> = {
  bug_fixes_improvements: {
    label: "Bug fixes and improvements",
    baseText: "Bug fixes and performance improvements.",
  },
  new_features: {
    label: "New features",
    baseText: "New features and improvements for a better experience.",
  },
};

type ReleaseNotesAIOutput = {
  releaseNotesByLocale?: Record<string, string>;
};

class GooglePlayReleaseNotesService {
  private isGooglePlaySupportedLocale(localeCode: string): boolean {
    return GOOGLE_PLAY_SUPPORTED_LOCALE_CODES.has(
      String(localeCode || "").trim().toLowerCase(),
    );
  }

  private toDate(value: unknown): Date {
    if (value instanceof Date) return value;
    if (value instanceof Timestamp) return value.toDate();
    const parsed = new Date(String(value || ""));
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }

  private generateReportId(): string {
    return `gp_release_notes_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 9)}`;
  }

  getTemplateLabel(templateKey: GooglePlayReleaseNotesTemplateKey): string {
    if (templateKey === "custom") return "Custom text";
    return TEMPLATE_LIBRARY[templateKey].label;
  }

  getTemplateBaseText(params: {
    templateKey: GooglePlayReleaseNotesTemplateKey;
    customText?: string;
  }): string {
    const customText = String(params.customText || "").trim();
    if (params.templateKey === "custom") {
      return customText;
    }
    return customText || TEMPLATE_LIBRARY[params.templateKey].baseText;
  }

  private buildPrompt(params: {
    app: App;
    locales: string[];
    baseText: string;
  }): string {
    const localeLines = params.locales
      .map((locale) => `- ${locale}: ${getStoreLocaleDisplayLabel(locale)}`)
      .join("\n");

    return [
      "You are generating Google Play release notes for multiple locales.",
      "Return concise, clear, policy-safe release notes per locale.",
      "Use plain UTF-8 text only. No markdown code blocks.",
      "Do not fabricate features not implied by the base text.",
      "",
      "App context:",
      ...buildAppContextLines({
        name: params.app.name,
        description: params.app.description,
        category: params.app.category,
        platform: params.app.platform,
        storeUrl: params.app.storeUrl,
        androidStoreUrl: params.app.androidStoreUrl,
        iosStoreUrl: params.app.iosStoreUrl,
        researchContext: params.app.researchContext,
      }).map((line) => `- ${line}`),
      "",
      `Base release note intent: ${params.baseText}`,
      "Localize naturally for each target locale below.",
      "Keep each locale output under 500 characters.",
      "",
      "Target locales:",
      localeLines,
      "",
      "Return JSON with exactly this shape:",
      '{ "releaseNotesByLocale": { "<localeCode>": "<localized release note>" } }',
      "Use the exact locale codes provided.",
    ].join("\n");
  }

  private chunkLocales(locales: string[], chunkSize: number): string[][] {
    const normalizedSize = Math.max(1, chunkSize);
    const chunks: string[][] = [];
    for (let index = 0; index < locales.length; index += normalizedSize) {
      chunks.push(locales.slice(index, index + normalizedSize));
    }
    return chunks;
  }

  private async generateBatchReleaseNotes(params: {
    app: App;
    locales: string[];
    baseText: string;
    model: string;
  }): Promise<Record<string, string>> {
    let lastError: unknown = null;

    for (let attempt = 1; attempt <= BATCH_RETRY_COUNT; attempt += 1) {
      try {
        const structured = await AIService.generateJSON<ReleaseNotesAIOutput>(
          this.buildPrompt({
            app: params.app,
            locales: params.locales,
            baseText: params.baseText,
          }),
          {
            model: params.model,
            temperature: 0.2,
            maxTokens: 4096,
            structuredOutputName: "google_play_release_notes",
            structuredOutputSchema: {
              type: "object",
              properties: {
                releaseNotesByLocale: {
                  type: "object",
                  additionalProperties: { type: "string" },
                },
              },
              required: ["releaseNotesByLocale"],
            },
          },
        );

        const generatedMap = structured?.releaseNotesByLocale || {};
        return Object.fromEntries(
          params.locales.map((locale) => [
            locale,
            String(generatedMap[locale] || "").trim(),
          ]),
        );
      } catch (error) {
        lastError = error;
      }
    }

    throw new Error(
      `Failed locale batch (${params.locales.join(", ")}): ${
        lastError instanceof Error ? lastError.message : "Unknown AI error"
      }`,
    );
  }

  async generateAndSaveReport(params: {
    userId: string;
    app: App;
    locales: string[];
    templateKey: GooglePlayReleaseNotesTemplateKey;
    customText?: string;
    model?: string;
  }): Promise<GooglePlayReleaseNotesReport> {
    const userId = String(params.userId || "").trim();
    if (!userId) throw new Error("User is required.");
    if (!params.app?.appId) throw new Error("A registered app is required.");

    const requestedLocales = Array.from(
      new Set((Array.isArray(params.locales) ? params.locales : []).map((item) => String(item || "").trim()).filter(Boolean)),
    );
    if (requestedLocales.length === 0) {
      throw new Error("Select at least one locale.");
    }
    const unsupportedLocales = requestedLocales.filter(
      (locale) => !this.isGooglePlaySupportedLocale(locale),
    );
    if (unsupportedLocales.length > 0) {
      throw new Error(
        `Unsupported Google Play locale(s): ${unsupportedLocales.join(", ")}`,
      );
    }
    const locales = requestedLocales;

    const baseText = this.getTemplateBaseText({
      templateKey: params.templateKey,
      customText: params.customText,
    });
    if (!baseText) {
      throw new Error("Provide base text for release notes.");
    }

    const modelUsed = String(params.model || DEFAULT_MODEL).trim() || DEFAULT_MODEL;
    const releaseNotesByLocale: Record<string, string> = {};
    const localeBatches = this.chunkLocales(locales, MAX_LOCALES_PER_BATCH);
    for (const localeBatch of localeBatches) {
      const batchMap = await this.generateBatchReleaseNotes({
        app: params.app,
        locales: localeBatch,
        baseText,
        model: modelUsed,
      });
      localeBatch.forEach((locale) => {
        releaseNotesByLocale[locale] = batchMap[locale] || baseText;
      });
    }

    const report: GooglePlayReleaseNotesReport = {
      reportId: this.generateReportId(),
      userId,
      appId: params.app.appId,
      appName: params.app.name,
      templateKey: params.templateKey,
      templateLabel: this.getTemplateLabel(params.templateKey),
      customText:
        params.templateKey === "custom"
          ? String(params.customText || "").trim()
          : String(params.customText || "").trim() || undefined,
      locales,
      releaseNotesByLocale,
      modelUsed,
      generatedAt: new Date(),
    };

    const reportPayload: Record<string, unknown> = {
      ...report,
      generatedAt: report.generatedAt,
      savedAt: serverTimestamp(),
    };
    if (!report.customText) {
      delete reportPayload.customText;
    }

    await addDoc(collection(db, REPORTS_COLLECTION), reportPayload);

    return report;
  }

  async getReportsByApp(params: {
    userId: string;
    appId: string;
  }): Promise<GooglePlayReleaseNotesReport[]> {
    const userId = String(params.userId || "").trim();
    const appId = String(params.appId || "").trim();
    if (!userId || !appId) return [];

    const reportsRef = collection(db, REPORTS_COLLECTION);
    const reportsQuery = query(
      reportsRef,
      where("userId", "==", userId),
      where("appId", "==", appId),
    );
    const snapshot = await getDocs(reportsQuery);

    return snapshot.docs
      .map((docItem) => {
        const data = docItem.data() as any;
        const locales = Array.isArray(data.locales)
          ? data.locales
              .map((locale: unknown) => String(locale || "").trim())
              .filter((locale: string) => this.isGooglePlaySupportedLocale(locale))
          : [];

        return {
          reportId: String(data.reportId || docItem.id),
          userId: String(data.userId || ""),
          appId: String(data.appId || ""),
          appName: String(data.appName || ""),
          templateKey: (data.templateKey ||
            "bug_fixes_improvements") as GooglePlayReleaseNotesTemplateKey,
          templateLabel: String(data.templateLabel || "Bug fixes and improvements"),
          customText: String(data.customText || "").trim() || undefined,
          locales,
          releaseNotesByLocale:
            data.releaseNotesByLocale && typeof data.releaseNotesByLocale === "object"
              ? Object.fromEntries(
                  Object.entries(data.releaseNotesByLocale).map(([locale, note]) => [
                    String(locale || "").trim(),
                    String(note || "").trim(),
                  ]),
                )
              : {},
          modelUsed: String(data.modelUsed || DEFAULT_MODEL),
          generatedAt: this.toDate(data.generatedAt || data.savedAt || new Date()),
          savedAt: data.savedAt ? this.toDate(data.savedAt) : undefined,
        } as GooglePlayReleaseNotesReport;
      })
      .sort((left, right) => right.generatedAt.getTime() - left.generatedAt.getTime());
  }
}

export default new GooglePlayReleaseNotesService();
